import React from "react";
import { Link } from "react-router-dom";
import { CA_HD_2027_REPORT_PATH } from "../../config/publishedReports";

export const GRIDPLAN_PLAN_PATH = "/news/gridplan";

const CLASSES = [
  {
    title: "Passenger EVs",
    body: "Adoption is driven by demographics, policy, and vehicle pricing. Travel uses the travel survey, demographics, and season and weather. Charging uses the rate plan, managed charging, charging infrastructure, and weather.",
  },
  {
    title: "Medium- and heavy-duty trucks",
    body: "A case selects up to five vehicle models, a rate plan, and weather, then runs adoption and charging-behavior models. Station overlays, managed charging including vehicle-to-grid, and a utility-region weather upload are still being finished.",
  },
  {
    title: "Transit buses",
    body: "Transit is part of the same forecast so bus charging is not left out of territory load. The transit workflow inside GridPlan is not open yet.",
  },
];

function GridPlanPlanArticle() {
  return (
    <article className="bg-white">
      <header className="border-b border-slate-200 bg-slate-50 pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="mx-auto max-w-3xl px-6">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wider">
            <span className="rounded-full bg-teal-50 px-2 py-0.5 text-teal-800">Roadmap</span>
            <span className="text-slate-400">October 2026</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500">For utilities</span>
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            GridPlan — AmpTrans's upcoming forecasting tool
          </h1>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            AmpTrans is building a utility load forecasting tool to see where EV charging load is headed and use that to identify infrastructure needs before the load arrives.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 pt-12">
        <h2 className="text-xl font-bold tracking-tight text-slate-900">One forecast, three vehicle classes</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {CLASSES.map((item) => (
            <section key={item.title} className="flex h-full flex-col rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5">
              <h3 className="text-base font-semibold text-slate-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.body}</p>
            </section>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 pb-8 md:pb-12">
        <h2 className="mt-12 text-xl font-bold tracking-tight text-slate-900">Where the load is shown</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-700">
          { /* TODO: add a visual, flesh out description */}
          Our forecast will show where load is likely to be heavy in the upcoming years. 
        </p>

        <h2 className="mt-12 text-xl font-bold tracking-tight text-slate-900">What is available now</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-700">
          The California heavy-duty forecast for 2027 is published and does not require an account. It is the preview of GridPlan’s load view while the rest of the tool is in development: total annual load, peak demand, and a hexagon-level charging profile.
        </p>
        <Link
          to={CA_HD_2027_REPORT_PATH}
          className="mt-5 inline-flex items-center rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-800"
        >
          Preview the California forecast
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-1.5">
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </Link>

        <h2 className="mt-12 text-xl font-bold tracking-tight text-slate-900">What is still being built</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-700">
          The workflow for registered utilities — selecting a territory, setting scenario inputs, and running an analysis — is in development. On the truck path, charging-station overlays and managed charging, including vehicle-to-grid, are not finished, and weather is not yet uploaded by utility region. The transit workflow is marked coming soon.
        </p>
        <p className="mt-3 text-base leading-relaxed text-slate-700">
          After the forecast itself, the next module is proactive planning: using the load picture to flag constraints and shape infrastructure investment before demand shows up on the system. That module is not part of the current forecast.
        </p>

        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-5">
          <p className="text-sm leading-relaxed text-slate-700">
            If you are a utility and want the forecast shaped around your territory, write to{" "}
            <a href="mailto:info@amptrans.com" className="font-semibold text-blue-700 hover:text-blue-800">
              info@amptrans.com
            </a>
            .
          </p>
          <Link to="/#news" className="mt-4 inline-flex text-sm font-semibold text-blue-700 hover:text-blue-800">
            Back to news
          </Link>
        </div>
      </div>
    </article>
  );
}

export default GridPlanPlanArticle;
